import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { checklistApi } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import { Plus, Pencil, Trash2, ListChecks, GripVertical } from 'lucide-react'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface VoceItem {
  id: string; testo: string; obbligatoria: boolean
  tipo?: 'checkbox' | 'testo' | 'select'
  opzioni?: string[]
  scelte_multiple?: boolean
  scelte_esclusive?: boolean
}
interface Template { id: number; nome: string; tipologia_wo: string; committente_id: number | null; voci: VoceItem[] }

const uid = () => Math.random().toString(36).slice(2, 10)

function SortableVoceRow({ v, idx, updateVoce, removeVoce, disableRemove }: {
  v: VoceItem; idx: number
  updateVoce: (id: string, changes: Partial<VoceItem>) => void
  removeVoce: (id: string) => void
  disableRemove: boolean
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: v.id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style} className="p-2 bg-slate-50 rounded-lg border border-slate-100 space-y-2">
      <div className="flex items-center gap-2">
        <span {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing shrink-0 touch-none">
          <GripVertical size={14} style={{ color: '#cbd5e1' }} />
        </span>
        <span className="text-xs text-slate-400 w-5 shrink-0">{idx + 1}.</span>
        <input className="input flex-1 text-sm py-1.5" value={v.testo}
          onChange={e => updateVoce(v.id, { testo: e.target.value })}
          placeholder="Descrizione attività da verificare..." />
        <select
          className="select text-xs py-1.5 w-32 shrink-0"
          value={v.tipo || 'checkbox'}
          onChange={e => updateVoce(v.id, {
            tipo: e.target.value as VoceItem['tipo'],
            opzioni: e.target.value === 'select' ? (v.opzioni ?? []) : undefined,
            scelte_multiple: e.target.value === 'select' ? (v.scelte_multiple ?? false) : undefined,
          })}
        >
          <option value="checkbox">✓/✗ Sì/No</option>
          <option value="select">☰ Lista scelte</option>
          <option value="testo">✏ Testo libero</option>
        </select>
        <label className="flex items-center gap-1 text-xs text-slate-600 shrink-0 cursor-pointer whitespace-nowrap">
          <input type="checkbox" checked={v.obbligatoria}
            onChange={e => updateVoce(v.id, { obbligatoria: e.target.checked })} />
          obbl.
        </label>
        <button onClick={() => removeVoce(v.id)} className="btn-ghost p-1 shrink-0"
          style={{ color: '#ef4444' }} disabled={disableRemove}>
          <Trash2 size={13} />
        </button>
      </div>
      {v.tipo === 'select' && (
        <div className="ml-7 space-y-1.5 border-t border-slate-200 pt-2">
          <label className="rpt-checkbox-row flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={!!v.scelte_multiple}
              onChange={e => updateVoce(v.id, { scelte_multiple: e.target.checked })} />
            Scelte multiple (il tecnico può selezionare più voci)
          </label>
          {v.scelte_multiple && (
            <label className="rpt-checkbox-row flex items-center gap-2 text-xs text-slate-600 cursor-pointer ml-5">
              <input type="checkbox" checked={!!v.scelte_esclusive}
                onChange={e => updateVoce(v.id, { scelte_esclusive: e.target.checked })} />
              Ma una sola alla volta (selezionandone una si deselezionano le altre)
            </label>
          )}
          <textarea
            className="input text-xs w-full"
            rows={3}
            placeholder={"Opzione 1\nOpzione 2\nOpzione 3"}
            value={(v.opzioni ?? []).join('\n')}
            onChange={e => updateVoce(v.id, { opzioni: e.target.value.split('\n') })}
          />
        </div>
      )}
    </div>
  )
}

function TemplateModal({ template, onClose, onSave, saving }: {
  template: Template | null; onClose: () => void
  onSave: (d: Omit<Template, 'id'>) => void; saving: boolean
}) {
  const [nome, setNome] = useState(template?.nome ?? '')
  const [tipologia, setTipologia] = useState(template?.tipologia_wo ?? '')
  const [voci, setVoci] = useState<VoceItem[]>(
    template?.voci?.length ? template.voci : [{ id: uid(), testo: '', obbligatoria: false, tipo: 'checkbox' }]
  )

  const addVoce = () => setVoci(p => [...p, { id: uid(), testo: '', obbligatoria: false, tipo: 'checkbox' as const }])
  const removeVoce = (id: string) => setVoci(p => p.filter(v => v.id !== id))
  const updateVoce = (id: string, changes: Partial<VoceItem>) =>
    setVoci(p => p.map(v => v.id === id ? { ...v, ...changes } : v))

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))
  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) return
    setVoci(p => {
      const oldIndex = p.findIndex(v => v.id === active.id)
      const newIndex = p.findIndex(v => v.id === over.id)
      return arrayMove(p, oldIndex, newIndex)
    })
  }

  const submit = () => {
    if (!nome.trim()) { toast.error('Nome obbligatorio'); return }
    if (!tipologia.trim()) { toast.error('Tipologia WO obbligatoria'); return }
    const valid = voci.filter(v => v.testo.trim()).map(v => ({
      ...v,
      opzioni: (v.opzioni ?? []).filter(Boolean),
    }))
    if (!valid.length) { toast.error('Aggiungi almeno una voce'); return }
    onSave({ nome: nome.trim(), tipologia_wo: tipologia.trim(), committente_id: null, voci: valid })
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="card w-full max-w-xl max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">
            {template ? 'Modifica Template' : 'Nuovo Template Checklist'}
          </h2>
          <button onClick={onClose} className="btn-ghost px-2">✕</button>
        </div>
        <div className="p-5 flex-1 overflow-y-auto space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Nome Template *</label>
              <input className="input" value={nome} onChange={e => setNome(e.target.value)}
                placeholder="es. Installazione ONT" />
            </div>
            <div>
              <label className="label">Tipologia WO *</label>
              <input className="input" value={tipologia} onChange={e => setTipologia(e.target.value)}
                placeholder="es. installazione_fibra" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label mb-0">Voci Checklist</label>
              <button onClick={addVoce} className="btn-ghost text-xs flex items-center gap-1" style={{ color: '#1E40AF' }}>
                <Plus size={13} /> Aggiungi voce
              </button>
            </div>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={voci.map(v => v.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {voci.map((v, idx) => (
                    <SortableVoceRow key={v.id} v={v} idx={idx} updateVoce={updateVoce} removeVoce={removeVoce}
                      disableRemove={voci.length === 1} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            <p className="text-xs text-slate-400 mt-1">
              Le voci <em>obbl.</em> bloccano l'avanzamento nel modal rapportino se non risposte.
            </p>
          </div>
        </div>
        <div className="p-5 border-t border-slate-100 flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary">Annulla</button>
          <button onClick={submit} className="btn-primary" disabled={saving}>
            {saving ? 'Salvataggio...' : template ? 'Salva Modifiche' : 'Crea Template'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Checklist() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const [modal, setModal] = useState<Template | null | undefined>(undefined)
  const [confirmDel, setConfirmDel] = useState<number | null>(null)

  const canEdit = ['admin', 'superadmin', 'dispatcher'].includes(user?.role ?? '')

  const { data: templates = [], isLoading } = useQuery<Template[]>({
    queryKey: ['checklist-templates'],
    queryFn: () => checklistApi.list().then(r => r.data),
  })

  const createMut = useMutation({
    mutationFn: (d: unknown) => checklistApi.create(d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['checklist-templates'] }); setModal(undefined); toast.success('Template creato') },
    onError: () => toast.error('Errore creazione'),
  })
  const updateMut = useMutation({
    mutationFn: ({ id, d }: { id: number; d: unknown }) => checklistApi.update(id, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['checklist-templates'] }); setModal(undefined); toast.success('Template aggiornato') },
    onError: () => toast.error('Errore aggiornamento'),
  })
  const deleteMut = useMutation({
    mutationFn: (id: number) => checklistApi.delete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['checklist-templates'] }); setConfirmDel(null); toast.success('Template eliminato') },
    onError: () => toast.error('Errore eliminazione'),
  })

  const handleSave = (data: Omit<Template, 'id'>) => {
    if (modal?.id) updateMut.mutate({ id: modal.id, d: data })
    else createMut.mutate(data)
  }
  const isSaving = createMut.isPending || updateMut.isPending

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="page-title">Checklist Intervento</h1>
          <p className="page-subtitle">Template checklist associati alle tipologie di work order</p>
        </div>
        {canEdit && (
          <button onClick={() => setModal(null)} className="btn-primary flex items-center gap-2">
            <Plus size={15} /> Nuovo Template
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="card p-8 text-center text-sm text-slate-400">Caricamento...</div>
      ) : templates.length === 0 ? (
        <div className="card p-12 text-center space-y-3">
          <ListChecks size={40} className="mx-auto" style={{ color: '#cbd5e1' }} />
          <p className="text-slate-500 font-medium">Nessun template configurato</p>
          <p className="text-slate-400 text-sm">Crea un template per associare una checklist a una tipologia di intervento.</p>
          {canEdit && (
            <button onClick={() => setModal(null)} className="btn-primary inline-flex items-center gap-2 mt-2">
              <Plus size={15} /> Crea il primo template
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map(t => (
            <div key={t.id} className="card p-4 flex items-start gap-4">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: '#eff6ff' }}>
                <ListChecks size={17} style={{ color: '#1E40AF' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-semibold text-sm text-slate-900">{t.nome}</span>
                  <span className="badge text-xs px-2 py-0.5 rounded-full"
                    style={{ background: '#eff6ff', color: '#1E40AF', border: '1px solid #bfdbfe' }}>
                    {t.tipologia_wo}
                  </span>
                </div>
                <p className="text-xs text-slate-500">
                  {t.voci?.length ?? 0} voci · {t.voci?.filter(v => v.obbligatoria).length ?? 0} obbligatorie
                </p>
                {t.voci?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {t.voci.slice(0, 5).map(v => (
                      <span key={v.id} className="text-xs px-2 py-0.5 rounded-full"
                        style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: v.obbligatoria ? '#dc2626' : '#64748b' }}>
                        {v.obbligatoria && '* '}{v.testo.length > 28 ? v.testo.slice(0, 28) + '…' : v.testo}
                      </span>
                    ))}
                    {t.voci.length > 5 && (
                      <span className="text-xs text-slate-400">+{t.voci.length - 5} altri</span>
                    )}
                  </div>
                )}
              </div>
              {canEdit && (
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => setModal(t)} className="btn-ghost p-2" title="Modifica">
                    <Pencil size={14} style={{ color: '#64748b' }} />
                  </button>
                  <button onClick={() => setConfirmDel(t.id)} className="btn-ghost p-2" title="Elimina">
                    <Trash2 size={14} style={{ color: '#ef4444' }} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modal !== undefined && (
        <TemplateModal template={modal} onClose={() => setModal(undefined)}
          onSave={handleSave} saving={isSaving} />
      )}

      {confirmDel !== null && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="card w-full max-w-sm p-6 space-y-4">
            <p className="font-semibold text-slate-900">Eliminare il template?</p>
            <p className="text-sm text-slate-500">
              I rapportini già inviati non vengono modificati.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmDel(null)} className="btn-secondary">Annulla</button>
              <button onClick={() => deleteMut.mutate(confirmDel!)} className="btn-primary"
                style={{ background: '#ef4444' }} disabled={deleteMut.isPending}>
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
